import t from 'react-translate';
import { useState } from 'react';
import { css } from '@emotion/react';
import { config } from '@config/pendo.config.json';
import NvPopover from 'shared/components/nv-popover';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import { useFormContext } from 'react-hook-form';
import { gray6 } from 'styles/global_defaults/colors';

const overlayStyles = css`
  max-width: 280px;
  .confirmation {
    max-width: 100%;
    .button-container {
      justify-content: center;
    }
  }
`;

const styles = css`
  &.disabled {
    .switch-container {
      background-color: ${gray6};
    }
  }
`;

type RequiredSwitchProps = {
  id: number;
  name: string;
  isRequired: boolean;
  setIsRequired: (isRequired: boolean) => void;
  isAttachedToRule: boolean;
  disabled: boolean;
};

const RequiredSwitch = ({ id, name, isRequired, setIsRequired, isAttachedToRule, disabled }: RequiredSwitchProps) => {
  const [isSwitchingRequired, setIsSwitchingRequired] = useState(false);
  const { setValue } = useFormContext();
  const translationBase = t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP;
  const popoverTranslationBase = translationBase.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER;

  const hardSetIsRequired = (value: any) => {
    setIsSwitchingRequired(value);
    setIsRequired(value);
  };

  const handleRequiredChange = (newState: boolean) => {
    setValue('isRequired', newState, {
      shouldValidate: true,
      shouldDirty: true,
    });
    if (!newState && isAttachedToRule) hardSetIsRequired(true);
    else setIsRequired(newState);
  };

  const dataQATag = config.pendo.athena.mentorshipProgram.settings.programProfileSetup.updateProgramLevelProfileQuestion;

  return (
    <NvPopover
      enabled
      show={isSwitchingRequired}
      overlayStyles={overlayStyles}
      content={(
        <NvConfirmationPopover
          onCancel={() => setIsSwitchingRequired(false)}
          onConfirm={() => hardSetIsRequired(false)}
          header={popoverTranslationBase.TITLE()}
          message={popoverTranslationBase.DESCRIPTION()}
          cancelBtnText={popoverTranslationBase.CANCEL()}
          confirmBtnText={popoverTranslationBase.OK()}
        />
      )}
      placement='top'
      rootClose
      onHide={() => setIsSwitchingRequired(false)}
      className='d-inline-flex mw-100'
      preventOverflow
    >
      <NvSwitch
        css={styles}
        disabled={disabled}
        checked={isRequired}
        className={`self-centered align-content-center ${disabled ? 'disabled' : ''}`}
        onChange={handleRequiredChange}
        dataQa={dataQATag}
        dataQaId={`${dataQATag}_${id}`}
        ariaLabel={
          isRequired
            ? translationBase.ARIA_LABEL.DISABLE_FIELD(name)
            : translationBase.ARIA_LABEL.ENABLE_FIELD(name)
        }
      />
    </NvPopover>
  );
};

export default RequiredSwitch;
